import React, { useState } from 'react';
import { SalvBanner, SalvBannerType, SalvButton, SalvDropdown, SalvModal, SalvTagsInput } from '@salvapp/ui-components';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { InternalApiAlertType } from '../../../api-internal';
import { getAlertTypeDropdownOptions } from '../../../alerts/alertTypeHelper';
import { convertToDropdownOption } from '../../../common/helper';
import { parseErrorMessage } from '../../../common/errors/helpers';
import { useAttachCaseAlerts } from './caseAlertQueryActions';
import { useDispatch } from 'react-redux';
import { showFeedback } from '../../../feedback/feedbackReducer';
import { useMe } from '../../../organisation/useMe';

interface AttachCaseAlertsModalProps {
  handle: string;
  show: boolean;
  onClose: () => void;
}

interface FormValues {
  alertType: InternalApiAlertType;
  alertIds: string[];
}

const validationSchema: Yup.Schema<FormValues> = Yup.object().shape({
  alertType: Yup.mixed<InternalApiAlertType>().required(),
  alertIds: Yup.array()
    .of(Yup.string().required())
    .test('all-numeric', 'All alert IDs must be valid numbers', (values: string[] | undefined) => {
      if (!values || values.length === 0) return true;
      return values.every((v: string) => !!v && !isNaN(Number.parseInt(v)));
    })
    .test('no-duplicates', 'Duplicate alert IDs are not allowed', (values: string[] | undefined) => {
      if (!values || values.length === 0) return true;
      const uniqueValues = new Set(values.map((v: string) => Number.parseInt(v)));
      return uniqueValues.size === values.length;
    })
    .required(),
});

const AttachCaseAlertsModal = ({ handle, show, onClose }: AttachCaseAlertsModalProps) => {
  const { features } = useMe();
  const dispatch = useDispatch();
  const attachCaseAlerts = useAttachCaseAlerts();

  const [error, setError] = useState<string | null>(null);

  const initialValues: FormValues = {
    alertType: InternalApiAlertType.SCREENING,
    alertIds: [],
  };

  const handleSubmit = async (values: FormValues, { resetForm }: FormikHelpers<FormValues>) => {
    const numericIds = values.alertIds.map((id) => parseInt(id, 10));

    setError(null);
    try {
      await attachCaseAlerts.mutateAsync({
        handle,
        alerts: numericIds.map((alertId) => ({ alertType: values.alertType, alertId })),
      });
      dispatch(showFeedback({ message: 'Alerts attached successfully', success: true }));
      onClose();
      resetForm();
    } catch (e) {
      const errorMessage = await parseErrorMessage(e);
      setError(errorMessage);
    }
  };

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
      {({ values, errors, setFieldValue, isValid, isSubmitting, resetForm }) => (
        <SalvModal show={show} onHide={onClose}>
          <Form>
            <SalvModal.Header>Attach Alerts</SalvModal.Header>
            <SalvModal.Body>
              {error && <SalvBanner message={error} type={SalvBannerType.DANGER} clearable className={'m-b-16'} />}
              <SalvDropdown
                label='Alert Type'
                options={getAlertTypeDropdownOptions(features)}
                value={convertToDropdownOption(values.alertType, values.alertType)}
                onChange={([option]) => setFieldValue('alertType', option.value as InternalApiAlertType)}
                clearable={false}
                className='m-b-16'
              />
              <SalvTagsInput
                value={values.alertIds}
                onChange={async (newValues) => {
                  await setFieldValue(
                    'alertIds',
                    newValues.map((v) => Number.parseInt(v).toString())
                  );
                }}
                label='Alert IDs'
                placeholder='Enter Alert ID'
                isInvalid={!!errors.alertIds}
              />
              {errors.alertIds && <div className={'salv-error-message'}>{errors.alertIds}</div>}
            </SalvModal.Body>
            <SalvModal.Footer>
              <SalvButton
                variant='muted'
                onClick={() => {
                  resetForm();
                  onClose();
                }}
                label='Cancel'
              />
              <SalvButton
                label='Attach'
                type={'submit'}
                disabled={values.alertIds.length === 0 || !isValid || isSubmitting || attachCaseAlerts.isPending}
              />
            </SalvModal.Footer>
          </Form>
        </SalvModal>
      )}
    </Formik>
  );
};

export default AttachCaseAlertsModal;
